import {
  SeqConsultBanner,
  SeqConsultBannerConfig,
  SeqConsultBannerKey,
} from '../model/vo/seq-consult';

/** 리모트 컨피그(display-service /api/v1/configs/{key}) 조회 키 */
export const SEQ_CONSULT_BANNER_CONFIG_KEY = 'seq-consult';

/** 화면이 지원하는 배너 슬롯 — 운영 데이터 화이트리스트 */
export const SEQ_CONSULT_BANNER_KEYS: SeqConsultBannerKey[] = [
  'STEP_FIRST',
  'STEP_SECOND',
  'PERSONAL_TOP',
  'EXIT_POPUP',
  'FINISH',
];

export type SeqConsultBannerChannel = 'PC' | 'MOBILE';

/**
 * 노출 일시 파싱 — HSMALL-17376
 *
 * `new Date(string)` 은 ISO 8601 형식만 동작이 스펙으로 보장된다. 그 외 포맷은
 * 브라우저마다 결과가 달라 운영자가 넣은 값이 특정 브라우저에서만 Invalid Date 가
 * 되는 사고가 난다. 그래서 형식을 먼저 검사하고, 통과한 값만 Date 로 만든다.
 *
 * 'YYYY-MM-DD HH:mm:ss'(공백 구분)은 실수로 자주 들어오는 형태라 T 로 정규화해 받되,
 * validateSeqConsultBannerConfig 가 NON_ISO_EXPOSURE_DATE 로 보고한다.
 * 오프셋이 없으면 사용자 기기 로컬 타임존으로 해석되므로 '+09:00' 명시를 권장한다.
 */
const EXPOSURE_DATE_PATTERN =
  /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}(:\d{2})?(\.\d+)?(Z|[+-]\d{2}:?\d{2})?$/;

export const parseExposureDate = (value?: string | null): Date | null => {
  const trimmed = value?.trim();

  if (!trimmed || !EXPOSURE_DATE_PATTERN.test(trimmed)) {
    return null;
  }

  // '2026-02-30' 같은 달력에 없는 날짜는 Invalid Date 가 아니라 다음 달로 넘어간다.
  // 운영자가 의도하지 않은 날짜로 조용히 노출되는 것을 막기 위해 직접 검사한다.
  const [year, month, day] = trimmed.slice(0, 10).split('-').map(Number);
  const calendar = new Date(Date.UTC(year, month - 1, day));

  if (
    calendar.getUTCFullYear() !== year ||
    calendar.getUTCMonth() !== month - 1 ||
    calendar.getUTCDate() !== day
  ) {
    return null;
  }

  const parsed = new Date(trimmed.replace(' ', 'T'));

  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

/**
 * 리모트 컨피그 value → 채널별 배너 목록 — HSMALL-17376
 *
 * 다음 항목은 버린다.
 * - 화이트리스트에 없는 bannerKey (운영 데이터 오타)
 * - 해당 채널 이미지 경로가 없는(null/공백) 항목 — EXIT_POPUP 처럼 한쪽 채널만
 *   운영하는 슬롯이 반대 채널에서 빈 이미지로 뜨는 것을 막는다.
 *
 * 미등록 key(data:null)·조회 실패·형태 불일치는 모두 빈 배열 → 화면은 기본 이미지를 쓴다.
 * 버려진 항목의 사유는 validateSeqConsultBannerConfig 로 확인한다.
 */
export const toSeqConsultBanners = (
  config: SeqConsultBannerConfig | null | undefined,
  channel: SeqConsultBannerChannel,
): SeqConsultBanner[] => {
  if (!Array.isArray(config)) {
    return [];
  }

  return config.reduce<SeqConsultBanner[]>((acc, item) => {
    const imageUrl = (
      channel === 'PC' ? item?.pcImageUrl : item?.moImageUrl
    )?.trim();

    if (!SEQ_CONSULT_BANNER_KEYS.includes(item?.bannerKey) || !imageUrl) {
      return acc;
    }

    acc.push({
      bannerKey: item.bannerKey,
      imageUrl,
      exposureStartAt: item.exposureStartAt,
      exposureEndAt: item.exposureEndAt,
    });

    return acc;
  }, []);
};

export type SeqConsultBannerConfigIssueReason =
  /** 화면이 모르는 배너 슬롯 — 오타 가능성 */
  | 'UNKNOWN_BANNER_KEY'
  /** 노출 일시 형식 오류 — 해당 배너가 영영 노출되지 않는다 */
  | 'INVALID_EXPOSURE_DATE'
  /** 공백 구분 등 비권장 형식 — 동작은 하나 오프셋 명시 권장 */
  | 'NON_ISO_EXPOSURE_DATE'
  /** 시작이 종료보다 늦음 — 해당 배너가 영영 노출되지 않는다 */
  | 'INVERTED_EXPOSURE_PERIOD';

export interface SeqConsultBannerConfigIssue {
  /** value 배열에서의 위치 */
  index: number;
  bannerKey?: string;
  reason: SeqConsultBannerConfigIssueReason;
  field?: 'exposureStartAt' | 'exposureEndAt';
}

/**
 * 운영 데이터 점검 — HSMALL-17376
 *
 * 폴백이 잘 되는 만큼 잘못된 설정이 장애로 드러나지 않는다(배너가 조용히 안 바뀐다).
 * 호출부에서 이 결과를 로깅해 원인을 남긴다.
 *
 * 채널별 이미지 누락은 정상 운영(한쪽 채널만 노출)과 구분이 안 되므로 여기서 보지 않는다.
 */
export const validateSeqConsultBannerConfig = (
  config: SeqConsultBannerConfig | null | undefined,
): SeqConsultBannerConfigIssue[] => {
  if (!Array.isArray(config)) {
    return [];
  }

  return config.reduce<SeqConsultBannerConfigIssue[]>((acc, item, index) => {
    if (!SEQ_CONSULT_BANNER_KEYS.includes(item?.bannerKey)) {
      acc.push({
        index,
        bannerKey: item?.bannerKey,
        reason: 'UNKNOWN_BANNER_KEY',
      });

      return acc;
    }

    const fields = ['exposureStartAt', 'exposureEndAt'] as const;

    fields.forEach((field) => {
      const raw = item?.[field];

      if (!raw) {
        return;
      }

      if (!parseExposureDate(raw)) {
        acc.push({
          index,
          bannerKey: item.bannerKey,
          reason: 'INVALID_EXPOSURE_DATE',
          field,
        });
        return;
      }

      if (raw.includes(' ') || !/(Z|[+-]\d{2}:?\d{2})$/.test(raw.trim())) {
        acc.push({
          index,
          bannerKey: item.bannerKey,
          reason: 'NON_ISO_EXPOSURE_DATE',
          field,
        });
      }
    });

    const startAt = parseExposureDate(item?.exposureStartAt);
    const endAt = parseExposureDate(item?.exposureEndAt);

    if (startAt && endAt && startAt.getTime() > endAt.getTime()) {
      acc.push({
        index,
        bannerKey: item.bannerKey,
        reason: 'INVERTED_EXPOSURE_PERIOD',
      });
    }

    return acc;
  }, []);
};

/**
 * 배너가 지금 노출 기간에 걸려 있는지 판정 — HSMALL-17376
 *
 * - 시작/종료 일시는 없으면 각각 하한/상한 없음으로 본다.
 * - 파싱 불가한 일시는 fail-closed(미노출) — 잘못된 운영 데이터로
 *   의도치 않은 이미지가 상시 노출되는 쪽이 더 위험하다.
 *   (조용히 묻히지 않도록 validateSeqConsultBannerConfig 로 보고한다)
 * - 경계값(시작·종료 시각 정각)은 노출에 포함한다.
 */
export const isExposedSeqConsultBanner = (
  banner: Pick<SeqConsultBanner, 'exposureStartAt' | 'exposureEndAt'>,
  now: Date = new Date(),
): boolean => {
  const current = now.getTime();

  if (Number.isNaN(current)) {
    return false;
  }

  if (banner.exposureStartAt) {
    const startAt = parseExposureDate(banner.exposureStartAt);
    if (!startAt || current < startAt.getTime()) {
      return false;
    }
  }

  if (banner.exposureEndAt) {
    const endAt = parseExposureDate(banner.exposureEndAt);
    if (!endAt || current > endAt.getTime()) {
      return false;
    }
  }

  return true;
};

/**
 * 배너 슬롯의 최종 이미지 경로 결정 — HSMALL-17376
 *
 * API 가 내려준 배너 중 해당 슬롯 + 노출 기간을 만족하는 첫 건을 쓰고,
 * 그 외(미응답 / 슬롯 없음 / 기간 미해당 / 빈 값)는 전부 defaultPath 로 폴백한다.
 * 기간이 겹치는 항목이 여럿이면 배열의 앞선 항목이 이긴다.
 *
 * 반환값은 CDN 루트 기준 상대 경로 — 환경별 도메인은 호출부에서 getCDNAsset 으로 붙인다.
 *
 * @param banners API 응답 배너 목록 (미로드 시 undefined)
 * @param bannerKey 배너 슬롯 키
 * @param defaultPath 현재 코드에 적용된 기본 이미지 경로
 */
export const resolveSeqConsultBannerPath = (
  banners: SeqConsultBanner[] | undefined,
  bannerKey: SeqConsultBannerKey,
  defaultPath: string,
  now: Date = new Date(),
): string => {
  const exposed = banners?.find(
    (banner) =>
      banner?.bannerKey === bannerKey &&
      !!banner?.imageUrl?.trim() &&
      isExposedSeqConsultBanner(banner, now),
  );

  return exposed?.imageUrl?.trim() || defaultPath;
};
